import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import LanguageSelector from './LanguageSelector';
import ContactFormModal from './ContactFormModal';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { localizedPath, locale } = useLocale();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (action) => {
    if (action === 'open_form:contact') {
      setIsContactFormOpen(true);
      setIsMobileMenuOpen(false);
    } else if (action.startsWith('scroll:')) {
      const targetId = action.split(':')[1];
      const currentPathWithoutLocale = window.location.pathname.split('/').slice(2).join('/');
      if (currentPathWithoutLocale !== '') {
        navigate(localizedPath(''));
        setTimeout(() => {
          const element = document.querySelector(targetId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur shadow-md' : 'bg-white'
        }`}
      >
        <div className="container">
          <div className="flex items-center justify-between py-3">
            {/* Logo */}
            <Link to={localizedPath('')} className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="Digital Corp France"
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <Link
                to={localizedPath('services')}
                className="text-sm font-medium text-gray-700 hover:text-[#0B3F84] transition-colors"
              >
                {t('nav.services')}
              </Link>
              <Link
                to={localizedPath('realisations')}
                className="text-sm font-medium text-gray-700 hover:text-[#0B3F84] transition-colors"
              >
                {t('nav.portfolio')}
              </Link>
              <Link
                to={localizedPath('assessment')}
                className="text-sm font-medium text-gray-700 hover:text-[#0B3F84] transition-colors"
              >
                {t('nav.assessment')}
              </Link>
              <Link
                to={localizedPath('blog')}
                className="text-sm font-medium text-gray-700 hover:text-[#0B3F84] transition-colors"
              >
                {t('nav.blog')}
              </Link>
              <LanguageSelector />
              <button
                onClick={() => handleNavClick('open_form:contact')}
                className="px-6 py-2 bg-[#0B3F84] text-white text-sm font-semibold rounded-lg hover:bg-[#0a3570] pulse-cta"
              >
                {t('nav.contact')}
              </button>
              <Link
                to={localizedPath('admin')}
                title="Admin"
                aria-label="Admin"
                className="p-2 text-gray-400 hover:text-[#0B3F84] transition-colors"
                data-testid="admin-shield-link"
              >
                <Shield size={20} />
              </Link>
            </div>

            {/* Mobile right side: shield + menu button */}
            <div className="md:hidden flex items-center gap-1">
              <Link
                to={localizedPath('admin')}
                title="Admin"
                aria-label="Admin"
                className="p-2 text-gray-400 hover:text-[#0B3F84] transition-colors"
                data-testid="admin-shield-link-mobile"
              >
                <Shield size={20} />
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-700"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="container py-4 flex flex-col gap-4">
              <Link
                to={localizedPath('services')}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-sm font-medium text-gray-700 hover:text-[#0B3F84] transition-colors py-2"
              >
                {t('nav.services')}
              </Link>
              <Link
                to={localizedPath('realisations')}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-sm font-medium text-gray-700 hover:text-[#0B3F84] transition-colors py-2"
              >
                {t('nav.portfolio')}
              </Link>
              <Link
                to={localizedPath('assessment')}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-sm font-medium text-gray-700 hover:text-[#0B3F84] transition-colors py-2"
              >
                {t('nav.assessment')}
              </Link>
              <Link
                to={localizedPath('blog')}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-sm font-medium text-gray-700 hover:text-[#0B3F84] transition-colors py-2"
              >
                {t('nav.blog')}
              </Link>
              <div className="py-2">
                <LanguageSelector />
              </div>
              <button
                onClick={() => handleNavClick('open_form:contact')}
                className="px-6 py-2.5 bg-[#0B3F84] text-white text-sm font-semibold rounded-lg hover:bg-[#0a3570] text-center"
              >
                {t('nav.contact')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </>
  );
};

export default Navigation;
